/**
 * Possible issues with the react-widgets DateTimePicker component:
 *
 * 1) tried to use onFocus, onToggle, and open props to open picker automatically when input
 * gets focus however it causes some glitchy behavior when clicking on the icons.  For example,
 * must click on the icon twice to expose the picker modal.
 *
 */
import classNames from 'classnames';
import { format } from 'date-fns';
import { connect } from 'formik';
import React, { Component, PropsWithChildren } from 'react';
// @ts-ignore
import dateFnsLocalizer from 'react-widgets-date-fns';
import DTP from 'react-widgets/lib/DateTimePicker';
import { RegisterElement } from '..';
import { getDescribedBy } from '../Input/inputUtils';
import { FieldHelpersWrapper } from '../utils/formHelpers';
import { resolveProps } from '../utils/formikUtils';
import { createId } from '../utils/IdGenerator';
import { sizeStyles } from '../utils/sizes';
import './DateTimePicker.scss';
import { IDateTimePickerProps, InnerDateTimePickerProps } from './types';

// context aware Localized formatting strings
const raw = {
  edit: {
    date: 'E, MMM d, yyyy', // Wed, Sep 4, 1986
    dateTime: 'E, MMM d, yyyy h:mm a', // Thu, Sep 4, 1986 8:30 PM
    time: 'h:mm a',
  },
  format: {
    date: 'E, MMM d, yyyy', // Wed, Sep 4, 1986
    dateTime: 'E, MMM d, yyyy h:mm a', // Thu, Sep 4, 1986 8:30 PM
    time: 'h:mm a', // 8:30 PM
  },
};

export interface IDateTimePickerState {
  open: boolean | 'date' | 'time';
  inputId: string;
  isValid: boolean;
}

/**
 * Beta
 *
 * DatePicker wraps: http://jquense.github.io/react-widgets/api/DateTimePicker
 * More of the API can be exposed if needed.
 */
export class DateTimePicker extends Component<PropsWithChildren<InnerDateTimePickerProps>, IDateTimePickerState> {
  public static defaultProps = {
    culture: 'en',
    date: true,
    ignoreFormik: false,
    label: 'Select a date',
    step: 30,
    time: false,
  };

  // create static formatter functions one time
  static get raw() {
    return raw;
  }

  public state: IDateTimePickerState = {
    inputId: '', // id comes from DateTimePicker input and is used in the htmlFor attr of the <label>
    isValid: true,
    open: false, // false | 'date' | 'time'
  };

  public role: 'date' | 'time' | 'dateTime';

  private id = createId('datepicker');

  constructor(props: InnerDateTimePickerProps) {
    super(props);

    // initialize the localization layer
    dateFnsLocalizer();

    const { date, time } = props;
    this.role = date && time ? 'dateTime' : date ? 'date' : 'time';
  }

  public componentDidMount() {
    this.props.formik.registerField && this.props.formik.registerField(this.props.name, this);
    window.document.addEventListener('keydown', this.onEscape, true);
  }

  public componentWillUnmount() {
    this.props.formik.unregisterField && this.props.formik.unregisterField(this.props.name);
    window.document.removeEventListener('keydown', this.onEscape, true);
  }

  public refreshIsValid = (date: any) => {
    const { formik } = this.props;
    const fieldName = resolveProps(this.props, this.props.formik).name;
    if (date && !(date instanceof Date)) {
      this.setState({ isValid: false });
      if (formik && formik.setFieldError) {
        formik.setFieldError(fieldName, 'Invalid date format');
      }
    } else {
      this.setState((state) => (state.isValid ? null : { isValid: true }));
    }
  };

  public handleChange = (date?: any, name?: string) => {
    const { formik } = this.props;
    this.refreshIsValid(date);

    if (this.props.onChange) {
      this.props.onChange(this.props.name, date);
    } else if (formik && formik.setFieldValue) {
      formik.setFieldValue(this.props.name, date || null);
    }
  };

  // open the dialog when DTP gets focus
  public handleFocus = () => {
    this.setState({ open: this.role === 'time' ? 'time' : 'date' });
  };

  public handleToggle = (open: any) => {
    // tslint:disable-line  using any because typing is wrong in @types/react-widget
    this.setState({ open });
  };

  // get the id that DTP assigns to the input field so we can assign it to the htmlFor attr of <label>
  // happens one time
  public getInputId = (target: HTMLDivElement) => {
    if (target && this.state.inputId === '') {
      const input = target.getElementsByClassName('rw-widget-input')[0];
      this.setState({ inputId: input.id });
    }
  };

  public render() {
    const { inputId = '' } = this.state;
    const { id } = this;
    const rawFormat = {
      edit: this.props.editDateFormat ? this.props.editDateFormat : raw.edit,
      format: this.props.viewDateFormat ? this.props.viewDateFormat : raw.format,
    };
    const {
      onChange,
      placeholder = 'e.g. ' + format(new Date(), rawFormat.edit[this.role]),
      label,
      helpText,
      required = false,
      sizePreset,
      ...props
    } = this.props;
    const { value, valid = true, touched = false, onBlur } = resolveProps(this.props, this.props.formik);
    const showError = (touched && !valid) || !this.state.isValid;
    const describedBy = getDescribedBy({
      hasErrors: showError,
      hasHelpText: !!helpText,
      id: inputId,
    });
    const classes = classNames('hlc-datetimepicker', `hlc-${this.role}`, {
      [`hlc-datetimepicker-open${this.state.open || ''}`]: !!this.state.open,
    });

    // need to make sure that label is a string for FieldHelpersWrapper
    const labelToString = label === false ? '' : label === true ? 'Select a date' : label;
    const normalizedProps = Object.assign({}, this.props, {
      label: labelToString,
    });
    // specifies date formats that the parse method should attempt to go through before invalidating format
    const parseFormats = [
      `${raw.format[this.role]}`,
      'M-DD-YYYY',
      'M-D-YYYY',
      'M-DD-YY',
      'MM-D-YYYY',
      'MM-D-YY',
      'M-D-YY',
      'L',
      'l',
    ];

    return (
      <RegisterElement name={`register-${id}`}>
        {(ref: any) => {
          const myRef = showError ? ref : undefined;
          return (
            <div id={`${id}-wrapper`} ref={myRef}>
              <div className={classes} ref={this.getInputId} qa-id={this.props['qa-id']} style={sizeStyles(sizePreset)}>
                <FieldHelpersWrapper id={inputId} props={normalizedProps}>
                  <div className={showError ? 'has-error' : undefined} aria-describedby={describedBy}>
                    <DTP
                      value={value || null}
                      format={rawFormat.format[this.role]}
                      editFormat={rawFormat.edit[this.role]}
                      timeFormat={rawFormat.format.time as any}
                      placeholder={placeholder}
                      onChange={this.handleChange}
                      // the ts-ignore is needed because the react-widgets dtp typing is bad
                      // @ts-ignore
                      onBlur={onBlur}
                      onToggle={this.onToggle}
                      open={this.state.open}
                      parse={parseFormats}
                      {...props}
                    />
                  </div>
                </FieldHelpersWrapper>
              </div>
            </div>
          );
        }}
      </RegisterElement>
    );
  }
  private onEscape = (e: KeyboardEvent) => {
    if (this.state.open && e.keyCode === 27) {
      this.setState({ open: false });
      e.stopPropagation();
    }
  };
  private onToggle = (data: boolean) => {
    this.setState({ open: data });
  };
}

export default connect<IDateTimePickerProps>(DateTimePicker);
